import { SearchIcon } from '@heroicons/react/outline';
import { debounce } from 'lodash';
import useLocale from 'next-translate/useTranslation';
import React, { useEffect, useMemo, useState } from 'react';
import Masonry from 'react-masonry-css';
import { Image } from 'types';

import { InfiniteScroller } from '@/components/atoms/InfinitiveScroller';
import GalleryMediaTile from '@/components/images/GalleryMediaTile';

import Button from '../buttons/Button';
import Spinner from '../Spinner';

interface UserImagesProps {
  initialImages: Image[];
  currentImage: string;
  searchQuery?: string;
  useLoadMore?: boolean;
}

const UserImages: React.FC<UserImagesProps> = ({
  initialImages,
  currentImage = '0',
  searchQuery,
  useLoadMore = false,
}) => {
  const [images, setImages] = useState<Image[]>(initialImages);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(2);
  const [search, setSearch] = useState<string | null>(searchQuery || null);
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useLocale('common');

  useEffect(() => {
    setImages(initialImages);
    setPage(2);
    setHasMore(true);
    setSearch(searchQuery || null);
  }, [initialImages]);

  // useEffect(() => {
  //   fetchMoreImages();
  // }, []);

  async function fetchMoreImages() {
    // Fetch more images from the server or API
    if (useLoadMore) {
      setIsLoading(true);
    }
    try {
      const response = await fetch(
        `/api/art-images?page=${page}${
          search ? `&search=${search}` : `&currentImage=${currentImage}`
        }`,
      );
      const moreImages: Image[] = await response.json();

      if (moreImages.length === 0) {
        setHasMore(false);
      } else {
        setImages((prevImages) => [...prevImages, ...moreImages]);
        setPage((prevPage) => prevPage + 1);
      }
    } catch (error) {
      console.error('Failed to fetch more images:', error);
    } finally {
      if (useLoadMore) {
        setIsLoading(false);
      }
    }
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    setPage(1);
  };

  useEffect(() => {
    if (search && search.length >= 3 && search !== searchQuery) {
      setImages([]);

      fetchMoreImages();
    }
  }, [search]);

  const memoizedHandleSearchChange = useMemo(() => {
    return debounce(handleSearchChange, 500);
  }, []);

  useEffect(() => {
    return () => {
      memoizedHandleSearchChange.cancel();
    };
  });

  const breakpointColumnsObj = {
    default: 6,
    1536: 5,
    1280: 4,
    1024: 3,
    768: 2,
    640: 2,
  };

  return (
    <section className='p-4 sm:py-16 justify-center mx-auto max-w-full'>
      <h2 className='text-2xl font-bold text-center mb-4'>
        {currentImage === '0'
          ? 'Example of generated art & images'
          : 'More images like this'}
      </h2>
      <div className='min-w-0 flex md:px-8 lg:px-0 xl:col-span-6 justify-center'>
        <div className='flex w-full items-center py-4 max-w-xl justify-center'>
          <div className='w-full'>
            <label htmlFor='search' className='sr-only'>
              {t('Search for images')}
            </label>
            <div className='relative'>
              <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
                <SearchIcon
                  className='h-5 w-5 text-gray-400'
                  aria-hidden='true'
                />
              </div>
              <input
                id='search'
                name='search'
                className='block w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm placeholder-gray-500 focus:border-primary-500 focus:text-gray-900 focus:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-0 dark:bg-slate-700/50 dark:text-white sm:text-sm'
                placeholder={t('Search for images') + '...'}
                type='search'
                onChange={memoizedHandleSearchChange}
              />
            </div>
          </div>
        </div>
      </div>
      <div>
        {page === 1 && images.length === 0 && search ? (
          <div className='flex justify-center mt-20 w-full'>
            <Spinner className='w-8 h-8 animate-spin text-primary-500' />
          </div>
        ) : useLoadMore ? (
          <>
            <Masonry
              breakpointCols={breakpointColumnsObj}
              className='flex w-auto -ml-4'
              columnClassName='pl-4 bg-clip-padding'
            >
              {images.map((image) => (
                <div key={image.slug} className='relative group mb-4'>
                  <GalleryMediaTile media={image} />
                </div>
              ))}
            </Masonry>
            {hasMore && (
              <div className='flex justify-center mt-8 w-full clear-both'>
                <Button
                  onClick={fetchMoreImages}
                  disabled={isLoading}
                  variant='outline'
                  isLoading={isLoading}
                >
                  <span>Load More</span>
                </Button>
              </div>
            )}
            {!hasMore && (
              <p className='text-center my-8'>The beginning of time...</p>
            )}
          </>
        ) : (
          <InfiniteScroller
            fetchNextPage={fetchMoreImages}
            hasNextPage={hasMore}
            loadingMessage={
              <div className='flex justify-center mt-8 w-full clear-both'>
                <Spinner className='w-8 h-8 animate-spin text-primary-500' />
              </div>
            }
            endingMessage={
              <p className='text-center my-8'>The beginning of time...</p>
            }
          >
            <Masonry
              breakpointCols={breakpointColumnsObj}
              className='flex w-auto -ml-4'
              columnClassName='pl-4 bg-clip-padding'
            >
              {images.map((image) => (
                <div key={image.slug} className='relative group mb-4'>
                  <GalleryMediaTile media={image} />
                </div>
              ))}
            </Masonry>
          </InfiniteScroller>
        )}
      </div>
    </section>
  );
};

export default UserImages;
